import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';

import homeHorse from '../assets/home-horse2.jpg';
import EnterDetails from '../assets/enter-details.png';
import Money from '../assets/money.jpg';
import QrCode from '../assets/qr-code.png';
import ScanCode from '../assets/scan-code.png';
import Calendar from '../assets/calendar.jpg';
import FarrierAppFeeds from '../assets/farrier-app-feeds.jpg';
import HiVizStrap from '../assets/hi-viz-strap.jpg';
import Janeone from '../assets/jane1.jpg';
import Homescreen from '../assets/home-screen.jpg';
import { LocalPrice } from '../components/local-price';
import { SHOP_PRODUCTS } from './shop/shop-products';
// import { StaticGoogleReviews } from '../components/google-reviews';

interface QuickEvent {
    id: number;
    calendar_title: string;
    calendar_date: string;
    equi_log_main?: {
        horse_name: string;
    };
}

interface HorseBoxAlert {
    type: 'MOT' | 'Insurance' | 'Service';
    date: string;
}

function Home(): React.JSX.Element {
    const [session, setSession] = useState<Session | null>(null);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [upcomingEvents, setUpcomingEvents] = useState<QuickEvent[]>([]);
    const [horseBoxAlerts, setHorseBoxAlerts] = useState<HorseBoxAlert[]>([]);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => setSession(session));

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
        });

        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        if (!session?.user?.id) {
            setHasActiveSubscription(false);
            setUpcomingEvents([]);
            setHorseBoxAlerts([]);
            return;
        }

        const fetchUpcomingAlerts = async () => {
            const todayStr = new Date().toISOString().split('T')[0];

            const { data: subscriptionData, error: subscriptionError } = await supabase
                .from('equi_subscriptions')
                .select('id')
                .eq('user_uuid', session.user.id)
                .eq('status', 'active')
                .limit(1);

            if (subscriptionError) {
                console.error('Homepage subscription check failed:', subscriptionError.message);
                setHasActiveSubscription(false);
                setUpcomingEvents([]);
                setHorseBoxAlerts([]);
                return;
            }

            const hasSubscription = !!subscriptionData?.length;
            setHasActiveSubscription(hasSubscription);

            if (!hasSubscription) {
                setUpcomingEvents([]);
                setHorseBoxAlerts([]);
                return;
            }

            // --- 1. Fetch 7-Day Calendar Events with Relational Horse Name Join ---
            const upperLimit7 = new Date();
            upperLimit7.setDate(upperLimit7.getDate() + 7);
            const upperLimit7Str = upperLimit7.toISOString().split('T')[0];

            const { data: calendarData, error: calendarError } = await supabase
                .from('equi_calendar')
                .select(
                    `
                    id, 
                    calendar_title, 
                    calendar_date,
                    equi_log_main (
                        horse_name
                    )
                `
                )
                .eq('user_uuid', session.user.id)
                .gte('calendar_date', todayStr)
                .lte('calendar_date', upperLimit7Str)
                .order('calendar_date', { ascending: true });

            if (calendarError) {
                console.error('Homepage calendar cache bypass log:', calendarError.message);
            } else if (calendarData) {
                setUpcomingEvents(calendarData as any[]);
            }

            // --- 2. Fetch Horsebox and Evaluate 30-Day Deadlines ---
            const upperLimit30 = new Date();
            upperLimit30.setDate(upperLimit30.getDate() + 30);
            const upperLimit30Str = upperLimit30.toISOString().split('T')[0];

            const { data: horseboxData } = await supabase
                .from('equi_horsebox')
                .select('mot_date, insurance_date, service_date')
                .eq('user_uuid', session.user.id)
                .maybeSingle();

            if (horseboxData) {
                const alerts: HorseBoxAlert[] = [];

                const isWithin30Days = (dateStr: string | null) => {
                    if (!dateStr) return false;
                    return dateStr >= todayStr && dateStr <= upperLimit30Str;
                };

                if (isWithin30Days(horseboxData.mot_date)) {
                    alerts.push({ type: 'MOT', date: horseboxData.mot_date! });
                }
                if (isWithin30Days(horseboxData.insurance_date)) {
                    alerts.push({ type: 'Insurance', date: horseboxData.insurance_date! });
                }
                if (isWithin30Days(horseboxData.service_date)) {
                    alerts.push({ type: 'Service', date: horseboxData.service_date! });
                }

                setHorseBoxAlerts(alerts);
            }
        };

        fetchUpcomingAlerts();
    }, [session]);

    return (
        <main className="page-wrapper">
            <div className="home-hero">
                <img src={homeHorse} alt="Equestrian Home" className="hero-bg-img" />
                <div className="centered-button">
                    {session ? (
                        <Link to="/dashboard" className="buttonOrange buttonMain">
                            Go to Your Stable
                        </Link>
                    ) : (
                        <>
                            <Link to="/login?mode=signup" className="buttonOrange buttonMain">
                                Sign up now!
                            </Link>
                            <Link to="/login" className="buttonPurple buttonMain">
                                Login to your account
                            </Link>
                        </>
                    )}
                </div>
            </div>

            <div className="page-container home-layout-grid">
                {session && hasActiveSubscription && (
                    <div className="section-container lightorange-section-container full-width">
                        <h2 className="textbig">Your upcoming events</h2>
                        {upcomingEvents.length > 0 ? (
                            <div>
                                <p className="marginbsixteen">
                                    <span>📅</span> -{' '}
                                    <strong className="text-normal">Next 7 Days:</strong>
                                </p>
                                <ul className="events-list">
                                    {upcomingEvents.map((event) => {
                                        const horseName = event.equi_log_main?.horse_name;
                                        return (
                                            <li key={event.id} className="marginbsixteen">
                                                <Link to={`/calendar`} className="text-normal">
                                                    {event.calendar_title} - for{' '}
                                                    {horseName ? `${horseName} - ` : ''}
                                                    <span>
                                                        (
                                                        {new Date(
                                                            event.calendar_date
                                                        ).toLocaleDateString(undefined, {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                        })}
                                                        )
                                                    </span>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : (
                            <>
                                <p className="marginbsixteen">
                                    <span>📅</span> -{' '}
                                    <strong className="text-normal">
                                        You have no events in the next 7 days.
                                    </strong>
                                </p>
                                <p>
                                    <Link to="/calendar">View Calendar</Link>
                                </p>
                            </>
                        )}
                    </div>
                )}

                {/* 🚛 UPCOMING HORSEBOX MAINTENANCE ALERTS (30 DAYS) */}
                {session && hasActiveSubscription && horseBoxAlerts.length > 0 && (
                    <div className="section-container lightorange-section-container full-width">
                        <h2 className="textbig">Horsebox Reminders</h2>
                        <div>
                            <p className="marginbsixteen">
                                <span>🚛</span> -{' '}
                                <strong className="text-normal">Due in the Next 30 Days:</strong>
                            </p>
                            <ul className="events-list">
                                {horseBoxAlerts.map((alert, index) => (
                                    <li key={index} className="marginbsixteen">
                                        <Link to={`/horsebox-view`} className="text-normal">
                                            ⚠️ Your Horsebox {alert.type} is due -{' '}
                                            <span>
                                                (
                                                {new Date(alert.date).toLocaleDateString(
                                                    undefined,
                                                    {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                    }
                                                )}
                                                )
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {!session && (
                    <div className="section-container white-section-container full-width">
                        <div className="about-split-row">
                            {/* Left Side: Text Column */}
                            <div className="about-text-col">
                                <h2 className="textbig marginbsixteen">
                                    Opening an account and adding your horse's details is 100% free.
                                </h2>
                                <p className="text-normal marginbsixteen">
                                    This is much more than a horse emergency QR code and TapTag. It's a
                                    complete, real-time stable help ecosystem for your horse.
                                </p>
                                <p className="text-normal marginbsixteen">
                                    Store as much information as you need without spending a penny.
                                </p>
                                <p className="text-normal marginbsixteen">
                                    If you want to activate your QR code or TapTag so others can scan and view
                                    your horse's details, it's less than{' '}
                                    <LocalPrice basePriceGbp={1} /> per month, billed annually. See?
                                    Proof that not everything in the horse world has to be
                                    expensive.
                                </p>
                                <p className="text-normal marginbsixteen">
                                    Safe Keeping: Even if you pause your subscription, we’ll safely
                                    retain all your horse’s information in your account so you don't
                                    lose your data.
                                </p>
                                <p className="text-normal marginbsixteen">
                                    <strong>What's not to like?</strong>
                                </p>
                                <p className="text-normal marginbsixteen">
                                    <Link to="/about-us">About us</Link>
                                </p>
                            </div>
                            {/* Right Side: Image Column */}
                            <img src={Homescreen} alt="Jane our horse" className="jane-image" />
                        </div>
                    </div>
                )}

                {!session && !hasActiveSubscription && (
                    <div className="section-container purple-section-container full-width">
                        <h1 className="textbig">
                            How it works if you subscribe for less than{' '}
                            <LocalPrice basePriceGbp={1} /> per month.
                        </h1>
                        <div className="info-bar-grid">
                            <div className="info-bar">
                                <div className="info-bar-fixed">
                                    <img src={EnterDetails} alt="Enter details" />
                                </div>
                                <div className="info-bar-column">
                                    <h2 className="textmedium">Upload your details</h2>
                                    <p className="text-normal">
                                        Fill in the information about your horse. Choose what you
                                        wish to show and what to keep hidden in your account area.
                                    </p>
                                </div>
                            </div>

                            <div className="info-bar">
                                <div className="info-bar-fixed">
                                    <img src={QrCode} alt="Get QR Code" />
                                </div>
                                <div className="info-bar-column">
                                    <h2 className="textmedium">Get your Stable card</h2>
                                    <p className="text-normal">
                                        Simply print it out and stick it on your stable. Or even get
                                        one of our TapTags for your stable. No need to scan.
                                    </p>
                                </div>
                            </div>

                            <div className="info-bar">
                                <div className="info-bar-fixed">
                                    <img src={ScanCode} alt="Access info" />
                                </div>
                                <div className="info-bar-column">
                                    <h2 className="textmedium">Access vital info instantly</h2>
                                    <p className="text-normal">
                                        Emergency contacts, medical details, and stable information
                                        are instantly accessible for both rider and horse.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <section className="latest-products-strip full-width">
                    <h2 className="textbig">Our latest products</h2>

                    <div className="latest-products-grid">
                        {[
                            { product: SHOP_PRODUCTS['travel-taptag'], title: 'High Gloss Travel TapTag' },
                            {
                                product: SHOP_PRODUCTS['high-gloss-stable-tag'],
                                title: 'High Gloss Stable Tag',
                            },
                            { product: SHOP_PRODUCTS['laminated-stable-tag'], title: 'Hi-Viz Wrist Straps' },
                        ].map(({ product, title }) => (
                            <Link
                                to={`/shop/${product.slug}`}
                                className="latest-product-card"
                                key={product.id}
                            >
                                <img
                                    src={product.image}
                                    alt={product.imageAlt}
                                    className="latest-product-image"
                                />
                                <span className="latest-product-info">
                                    <span className="latest-product-name">
                                        {title} - {product.price}
                                    </span>
                                    <span className="latest-product-linker">See full details</span>
                                </span>
                            </Link>
                        ))}
                    </div>
                    <p className="marginbsixteen margintsixteen">
                        <Link to="/shop">Visit the shop to see all products</Link>
                    </p>
                </section>

                <section className="product-feature-updates full-width">
                    <h2 className="textbig">Product and feature updates</h2>
                    <div className="product-feature-updates-grid">
                        <Link
                            to="/shop/high-viz-wrist-straps"
                            className="product-feature-update-card"
                        >
                            <img
                                src={HiVizStrap}
                                alt="Hi-Viz wrist strap with a QR code"
                                className="product-feature-update-image"
                            />
                            <span className="product-feature-update-info">
                                <span className="latest-product-name">
                                    Hi-Viz Wrist Straps
                                </span>
                                <span className="latest-product-linker">
                                    Keep your horse's details close at hand when hacking or
                                    eventing.
                                </span>
                                <span className="product-feature-update-link">Shop now</span>
                            </span>
                        </Link>
                        <Link
                            to="/adding-a-farrier-entry"
                            className="product-feature-update-card"
                        >
                            <img
                                src={FarrierAppFeeds}
                                alt="Farrier appointment and horse care information"
                                className="product-feature-update-image"
                            />
                            <span className="product-feature-update-info">
                                <span className="latest-product-name">
                                    Adding a farrier entry
                                </span>
                                <span className="latest-product-linker">
                                    See how to add a farrier appointment to your NeighTag
                                    calendar.
                                </span>
                                <span className="product-feature-update-link">Read more</span>
                            </span>
                        </Link>
                    </div>
                </section>
                <div className="section-container white-section-container full-width">
                    <div className="about-split-row">
                        {/* Left Side: Text Column */}
                        <div className="about-text-col">
                            <h3 className="textmedium marginbeight">Meet Jane, Our Inspiration</h3>
                            <p className="text-normal marginbsixteen">
                                Our absolute pride and joy is <strong>Jane</strong>, our Irish
                                Sports Horse. She is the true inspiration behind everything we do
                                here.
                            </p>
                            <p className="text-normal marginbsixteen">
                                Jane loves nothing more than leaving a massive gallop track across
                                an open field, tackling bold cross-country lines, and jumping her
                                heart out. She keeps us on our toes, teaches us something new every
                                day, and reminds us why we fell in love with horses in the first
                                place.
                            </p>
                            <p className="text-normal marginbsixteen">
                                <Link to="/about-us">Read more about us</Link>
                            </p>
                        </div>

                        {/* Right Side: Image Column */}
                        <img src={Janeone} alt="Jane our horse" className="jane-image" />
                    </div>
                </div>

                {!session && (
                    <>
                        <div className="section-container white-section-container split-card">
                            <h2 className="textbig">How much does it cost.</h2>
                            <div className="pricing-content-wrapper">
                                <img
                                    className="marginbsixteen pricing-img"
                                    src={Money}
                                    alt="Pricing"
                                />
                                <div className="pricing-text">
                                    <p className="marginbsixteen">
                                        Opening an account and adding details is free. You can store
                                        all the information you need for your horse and we wont need
                                        a penny.
                                    </p>
                                    <p className="marginbsixteen">
                                        If you wish to share your horse details via the QR code, use
                                        the calendar and horsebox feature it will be{' '}
                                        <strong>
                                            less than <LocalPrice basePriceGbp={1} /> a month.
                                        </strong>
                                    </p>
                                    <p className="marginbsixteen">
                                        See, not all horse related things are expensive.
                                    </p>
                                    <p className="marginbsixteen">
                                        <LocalPrice basePriceGbp={11} /> will be charge for a 12
                                        month subscription. Your details will be retained for you
                                        even if you dont re-subscribe.
                                    </p>
                                    <p className="marginbsixteen">
                                        <strong>Whats not to like?</strong>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="section-container white-section-container split-card">
                            <h2 className="textbig">Your extras.</h2>
                            <div className="pricing-content-wrapper">
                                <img
                                    className="marginbsixteen pricing-img"
                                    src={Calendar}
                                    alt="Calendar setup"
                                />
                                <div className="pricing-text">
                                    <p className="text-normal marginbsixteen">
                                        When you sign up for our paid service, you can add your
                                        schedule to your NeighTag calendar. Clinics on Thursday,
                                        farrier next week, dentist in 4 weeks. Whatever you have,
                                        you can add it to our NeighTag calendar.
                                    </p>
                                    <p className="text-normal marginbsixteen">
                                        Get a shareable QR code. Also add your horse box details,
                                        insurance, breakdown, MOT dates etc.
                                    </p>
                                    <p className="text-normal marginbsixteen">
                                        Get a reminder a few days before so you dont forget those
                                        important dates.
                                    </p>
                                    {!session && (
                                        <Link
                                            to="/login?mode=signup"
                                            className="buttonWhite buttonMain"
                                        >
                                            Sign up now!
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <div className="section-container full-width" style={{ textAlign: 'center' }}>
                    <p className="text-normal">
                        Thank you for being part of the NeighTag family. We’ll see you out on the
                        cross-country course!
                    </p>
                    <p
                        className="text-purple marginbsixteen"
                        style={{ fontWeight: 'bold', marginTop: '8px', fontSize: '1.2rem' }}
                    >
                        — The NeighTag Family (Mandy, Abs and Jane! 🥕)
                    </p>

                    <Link to="/add-bookmark">Add a bookmark to your phones home screen.</Link>
                </div>

                {/*<div className="section-container white-section-container full-width">*/}
                {/*    <h4 className="textmedium">Customer Feedback</h4>*/}
                {/*    <StaticGoogleReviews />*/}
                {/*</div>*/}
            </div>
        </main>
    );
}

export default Home;
